import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";

import MaleBanner   from "../../assets/img/website-images/male-teacher-gradient.png";
import FemaleBanner from "../../assets/img/website-images/male-teacher-gradient.png";

/* ─── Gender image helper ──────────────────────────────────────── */
const getImage = (name) => {
    const lower = name.toLowerCase();
    if (
        lower.startsWith("mr") ||
        lower.startsWith("dr") ||
        lower.includes(" kumar") ||
        lower.includes(" reddy") ||
        lower.includes("byra") ||
        lower.includes("vishwas") ||
        lower.includes("charan") ||
        lower.includes("sunil") ||
        lower.includes("sumanth") ||
        lower.includes("gokula") ||
        lower.includes("lokesh") ||
        lower.includes("bharath")
    ) {
        return MaleBanner;
    }
    return FemaleBanner;
};

/* ─── Data from screenshot ─────────────────────────────────────── */
const principal = [
    { name: "Dr Bhavya MV", role: "Principal & Professor" },
];

const teachingStaff = [
    { name: "Mrs Shilpa Murthy",    role: "Associate Professor"  },
    { name: "Mr Gokula Krishnan S", role: "Assistant Professor"  },
    { name: "Mrs Usha A",           role: "Assistant Professor"  },
    { name: "Mr Bharath kumar",     role: "Assistant Professor"  },
    { name: "Mrs Gayana PM",        role: "Assistant Professor"  },
    { name: "Mr Sunil US",          role: "Assistant Professor"  },
    { name: "Dr Lokesh SV",         role: "Assistant Professor"  },
    { name: "Dr Shafna S",          role: "Assistant Professor"  },
    { name: "Miss Keerthi BV",      role: "Assistant Professor"  },
    { name: "Mr Sumanth G",         role: "Assistant Professor"  },
    { name: "Miss Bhagyashree M",   role: "Assistant Professor"  },
    { name: "Miss Bhavana SN",      role: "Assistant Professor"  },
];

const nonTeachingStaff = [
    { name: "Mr Kumar KP",  role: "Lab Technician – Pharmaceutical Chemistry" },
    { name: "Mr Charan",    role: "Lab Technician – Pharmaceutics"            },
    { name: "Sandhiya",     role: "Lab Technician – Pharmacology"             },
    { name: "Byra reddy",   role: "Lab Technician – Pharmacognosy"            },
    { name: "Mr Vishwas",   role: "Lab Technician – Pharmaceutical Chemistry" },
];

/* ─── Variants ─────────────────────────────────────────────────── */
const sectionTitleVariants = {
    hidden: { opacity: 0, y: -35 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
};

const gridContainerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
};

const cardVariants = {
    hidden: { opacity: 0, y: 45, scale: 0.94 },
    visible: {
        opacity: 1, y: 0, scale: 1,
        transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
};

/* ─── Single Staff Card ────────────────────────────────────────── */
function StaffCard({ teacher }) {
    const imgRef = useRef(null);
    const { ref: inViewRef, inView } = useInView({ threshold: 0.2, triggerOnce: true });

    useEffect(() => {
        if (inView && imgRef.current) {
            gsap.fromTo(
                imgRef.current,
                { scale: 0.85, opacity: 0 },
                { scale: 1, opacity: 1, duration: 0.65, ease: "back.out(1.4)", delay: 0.1 }
            );
        }
    }, [inView]);

    const cardRef = useRef(null);
    const handleEnter = () => {
        gsap.to(cardRef.current, { y: -8, duration: 0.3, ease: "power2.out" });
        gsap.to(imgRef.current,  { scale: 1.06, duration: 0.4, ease: "power2.out" });
    };
    const handleLeave = () => {
        gsap.to(cardRef.current, { y: 0, duration: 0.3, ease: "power2.out" });
        gsap.to(imgRef.current,  { scale: 1, duration: 0.4, ease: "power2.out" });
    };

    const setRefs = (el) => {
        cardRef.current = el;
        inViewRef(el);
    };

    return (
        <motion.div variants={cardVariants}>
            <div
                ref={setRefs}
                className="single-team mb-40"
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                style={{ cursor: "default" }}
            >
                <div className="team-thumb">
                    <div className="brd">
                        <Link to="#">
                            <img
                                ref={imgRef}
                                src={getImage(teacher.name)}
                                alt={teacher.name}
                                style={{ opacity: 0 }}
                            />
                        </Link>
                    </div>
                </div>
                <div className="team-info text-center">
                    <motion.h4
                        initial={{ opacity: 0, y: 10 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.45, delay: 0.3 }}
                    >
                        {teacher.name}
                    </motion.h4>
                    <motion.p
                        initial={{ opacity: 0, y: 8 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.4, delay: 0.4 }}
                    >
                        {teacher.role}
                    </motion.p>
                </div>
            </div>
        </motion.div>
    );
}

/* ─── Section with title + grid ────────────────────────────────── */
function StaffSection({ title, members, colClass = "col-xl-3 col-md-6" }) {
    const { ref: titleRef, inView: titleInView } = useInView({ threshold: 0.2, triggerOnce: true });
    const { ref: gridRef,  inView: gridInView  } = useInView({ threshold: 0.1, triggerOnce: true });

    return (
        <div className="row mt-80">
            <div className="col-12 text-center mb-50" ref={titleRef}>
                <motion.h2
                    variants={sectionTitleVariants}
                    initial="hidden"
                    animate={titleInView ? "visible" : "hidden"}
                >
                    {title}
                </motion.h2>
            </div>

            <motion.div
                ref={gridRef}
                className="row"
                style={{ width: "100%" }}
                variants={gridContainerVariants}
                initial="hidden"
                animate={gridInView ? "visible" : "hidden"}
            >
                {members.map((teacher, i) => (
                    <div key={i} className={colClass}>
                        <StaffCard teacher={teacher} />
                    </div>
                ))}
            </motion.div>
        </div>
    );
}

/* ─── Principal Section ─────────────────────────────────────────── */
function PrincipalSection() {
    const { ref: titleRef, inView: titleInView } = useInView({ threshold: 0.2, triggerOnce: true });
    const { ref: cardRef,  inView: cardInView  } = useInView({ threshold: 0.2, triggerOnce: true });

    const imgRef    = useRef(null);
    const cardElRef = useRef(null);

    useEffect(() => {
        if (cardInView && imgRef.current) {
            gsap.fromTo(
                imgRef.current,
                { scale: 0.85, opacity: 0 },
                { scale: 1, opacity: 1, duration: 0.75, ease: "back.out(1.6)", delay: 0.2 }
            );
        }
    }, [cardInView]);

    const handleEnter = () => {
        gsap.to(cardElRef.current, { y: -10, duration: 0.3, ease: "power2.out" });
        gsap.to(imgRef.current,    { scale: 1.06, duration: 0.4, ease: "power2.out" });
    };
    const handleLeave = () => {
        gsap.to(cardElRef.current, { y: 0, duration: 0.3, ease: "power2.out" });
        gsap.to(imgRef.current,    { scale: 1, duration: 0.4, ease: "power2.out" });
    };

    return (
        <>
            <motion.h2
                ref={titleRef}
                variants={sectionTitleVariants}
                initial="hidden"
                animate={titleInView ? "visible" : "hidden"}
            >
                Principal
            </motion.h2>

            <div className="col-12 d-flex justify-content-center mt-4">
                <div className="col-xl-3 col-md-6">
                    <motion.div
                        ref={cardRef}
                        variants={cardVariants}
                        initial="hidden"
                        animate={cardInView ? "visible" : "hidden"}
                    >
                        <div
                            ref={cardElRef}
                            className="single-team mb-40"
                            onMouseEnter={handleEnter}
                            onMouseLeave={handleLeave}
                            style={{ cursor: "default" }}
                        >
                            <div className="team-thumb">
                                <div className="brd">
                                    <Link to="#">
                                        <img
                                            ref={imgRef}
                                            src={getImage(principal[0].name)}
                                            alt={principal[0].name}
                                            style={{ opacity: 0 }}
                                        />
                                    </Link>
                                </div>
                            </div>
                            <div className="team-info text-center">
                                <motion.h4
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={cardInView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ duration: 0.45, delay: 0.35 }}
                                >
                                    {principal[0].name}
                                </motion.h4>
                                <motion.p
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={cardInView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ duration: 0.4, delay: 0.45 }}
                                >
                                    {principal[0].role}
                                </motion.p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function Expert() {
    return (
        <section className="team-area2 fix p-relative pt-120 pb-80">
            <div className="container">

                {/* Principal */}
                <div className="row">
                    <div className="col-12 text-center mb-50">
                        <PrincipalSection />
                    </div>
                </div>

                {/* Teaching Staff */}
                <StaffSection
                    title="Teaching Staff"
                    members={teachingStaff}
                    colClass="col-xl-3 col-md-6"
                />

                {/* Non-Teaching Staff */}
                <StaffSection
                    title="Non-Teaching Staff"
                    members={nonTeachingStaff}
                    colClass="col-xl-3 col-md-6"
                />

            </div>
        </section>
    );
}

export default Expert;
